"use client"

import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { normalizeUnitType } from "@/lib/util/normalize-unit"
import { resolveImageUrl } from "@/lib/util/image-url"
import type { FormQuestion, TextFormQuestion } from "@/types/formQuestion"
import { StoreProductVariant } from "@/types/store"
import { Button, Heading } from "@medusajs/ui"
import Input from "@modules/common/components/input"
import Modal from "@modules/common/components/modal"
import { Minus, Plus, ShoppingCart } from "lucide-react"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

type AddToCartModalProps = {
  isOpen: boolean
  closeModal: () => void
  handleAddToCart: (answers: { [key: string]: any }) => Promise<null | undefined>
  variant: StoreProductVariant
  quantity: number
  setQuantity: React.Dispatch<React.SetStateAction<number>>
  isAdding: boolean
  selectedVariant: StoreProductVariant
  maxQuantity?: number,
  productUnitTypeName?: string | null
}

const AddToCartModal = ({
  isOpen,
  handleAddToCart,
  closeModal,
  quantity,
  setQuantity,
  isAdding,
  selectedVariant,
  maxQuantity,
  productUnitTypeName
}: AddToCartModalProps) => {
  const { t } = useTranslation()
  const [error, setError] = useState("")
  const [formAnswers, setFormAnswers] = useState<{ [key: string]: any }>({})
  const [isFormValid, setIsFormValid] = useState(true)
  const [formFieldErrors, setFormFieldErrors] = useState<{ [key: string]: string }>({})
  const computedMax = Number.isFinite(maxQuantity as number)
    ? (maxQuantity as number)
    : Number.MAX_SAFE_INTEGER
  const showControls = computedMax > 0

  useEffect(() => {
    if (selectedVariant?.form_questions) {
      const initialAnswers: { [key: string]: any } = {}
      selectedVariant.form_questions.forEach((q: FormQuestion, idx: number) => {
        const key = q.id || idx
        const initialValue =
          q.type === "text" && (q as TextFormQuestion).defaultValue != null
            ? normalizeTextEncoding((q as TextFormQuestion).defaultValue ?? "")
            : formAnswers[key] ?? ""
        initialAnswers[key] = initialValue
      })
      setFormAnswers(initialAnswers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  const getQuestionKey = (q: FormQuestion, idx: number) => q.id || idx

  function isTextFormQuestion(q: FormQuestion): q is TextFormQuestion {
    return q.type === "text"
  }

  const validateForm = () => {
    if (!selectedVariant?.form_questions) return true
    for (let i = 0; i < selectedVariant.form_questions.length; i++) {
      const q = selectedVariant.form_questions[i]
      const key = getQuestionKey(q, i)
      if (q.type === "text") {
        const { label, required } = q
        if (required && !formAnswers[key]?.toString().trim()) {
          setError(t("product.fieldRequired", { field: normalizeTextEncoding(label || "") }))
          return false
        }
      }
    }
    setError("")
    return true
  }

  useEffect(() => {
    if (!selectedVariant?.form_questions) {
      setIsFormValid(true)
      setFormFieldErrors({})
      return
    }
    let valid = true
    const newErrors: { [key: string]: string } = {}
    for (let i = 0; i < selectedVariant.form_questions.length; i++) {
      const q = selectedVariant.form_questions[i]
      const key = getQuestionKey(q, i)
      if (q.type === "text" && q.required) {
        const value = formAnswers[key]?.toString() || ""
        if (!value.trim()) {
          valid = false
          newErrors[key] = t("product.fieldRequired", { field: normalizeTextEncoding(q.label || "") })
        } else if (typeof q.minLength === "number" && value.length < q.minLength) {
          valid = false
          newErrors[key] = t("product.fieldMinLength", { field: normalizeTextEncoding(q.label || ""), min: q.minLength })
        } else {
          newErrors[key] = ""
        }
      }
    }
    setFormFieldErrors(newErrors)
    setIsFormValid(valid)
  }, [formAnswers, selectedVariant, t])

  const handleIncrement = () => {
    setQuantity((prev: number) => Math.min(prev + 1, computedMax))
  }

  const handleDecrement = () => {
    setQuantity((prev: number) => (prev > 1 ? prev - 1 : 1))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    const textAnswers = selectedVariant?.form_questions
      ?.filter((q: FormQuestion) => q.type === "text")
      .map((q: FormQuestion, idx: number) => {
        const key = getQuestionKey(q, idx)
        return {
          id: q.id ?? idx,
          label: isTextFormQuestion(q) ? normalizeTextEncoding(q.label || "") : "",
          answer: normalizeTextEncoding(formAnswers[key] || ""),
        }
      }) ?? []

    handleAddToCart(textAnswers)
  }

  const totalPrice = (() => {
    let unitPrice = selectedVariant?.sales_price ?? 0
    if (
      selectedVariant?.stocks &&
      selectedVariant.stocks.length > quantity - 1 &&
      selectedVariant.stocks[quantity - 1] &&
      typeof selectedVariant.stocks[quantity - 1].sales_price === "number"
    ) {
      unitPrice = selectedVariant.stocks[quantity - 1].sales_price
    }
    return (unitPrice * quantity).toLocaleString("tr-TR", {
      style: "currency",
      currency: "TRY",
    })
  })()

  return (
    <Modal isOpen={isOpen} close={closeModal} size="large">
      <Modal.Title>
        <div className="flex flex-col gap-0.5 pr-8">
          <span className="text-xs font-medium uppercase tracking-wide text-orange-600">
            {t("product.preview")}
          </span>
          <Heading className="text-xl font-semibold leading-tight text-foreground">
            {normalizeTextEncoding(selectedVariant?.title ?? "")}
          </Heading>
        </div>
      </Modal.Title>
      <form className="flex flex-col overflow-y-auto" onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="flex w-full flex-col gap-5">
            {selectedVariant.images?.[0]?.image_key && (
              <div className="overflow-hidden rounded-xl border border-muted-accent/60 bg-muted/30">
                <Image
                  width={400}
                  height={240}
                  src={resolveImageUrl(selectedVariant.images[0].image_key) || ""}
                  alt={selectedVariant.title || ""}
                  className="h-48 w-full object-cover sm:h-56"
                />
              </div>
            )}

            {selectedVariant?.form_questions
              ?.filter((q) => q.type === "info")
              .map((q, idx) => {
                const key = getQuestionKey(q, idx)
                return (
                  <div
                    key={key}
                    className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm text-blue-800"
                  >
                    {normalizeTextEncoding(q.message || "")}
                  </div>
                )
              })}

            {selectedVariant?.form_questions
              ?.filter((q) => q.type === "image")
              .map((q, idx) => {
                const key = getQuestionKey(q, idx)
                return (
                  <div key={key}>
                    {q.imageKey && (
                      <Image
                        width={400}
                        height={200}
                        src={`${process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL}${q.imageKey}`}
                        alt={normalizeTextEncoding(q.label || "")}
                        className="w-full max-h-48 rounded-xl object-cover"
                      />
                    )}
                    {q.description && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {normalizeTextEncoding(q.description)}
                      </p>
                    )}
                  </div>
                )
              })}

            {selectedVariant?.form_questions?.some((q) => q.type === "text") && (
              <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                {selectedVariant.form_questions.map((q: FormQuestion, idx: number) => {
                  const key = getQuestionKey(q, idx)
                  if (q.type !== "text") return null
                  const textQ = q as TextFormQuestion
                  const displayValue = formAnswers[key] ?? ""
                  const placeholderText = displayValue
                    ? ""
                    : normalizeTextEncoding(textQ.placeholder ?? "")
                  return (
                    <div key={key}>
                      <Input
                        topLabel={normalizeTextEncoding(textQ.label || "")}
                        name={String(q.id ?? idx)}
                        required={q.required}
                        placeholder={placeholderText}
                        data-testid={q.defaultValue || q.label || `text-question-${q.id}`}
                        type="text"
                        minLength={q.minLength}
                        maxLength={q.maxLength}
                        value={displayValue}
                        onChange={(e) =>
                          setFormAnswers((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        className="w-full rounded-lg border-2 border-muted-accent bg-background p-2 focus-visible:outline-none focus-visible:ring-0 dark:bg-bg-body"
                      />
                      {formFieldErrors[key] && (
                        <p className="mt-1 text-xs text-red-500">{formFieldErrors[key]}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="rounded-xl border border-muted-accent/80 bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">
                    {productUnitTypeName ?
                      t(`cartDropdown.unitTypes.${normalizeUnitType(productUnitTypeName)}` as any) : t("product.quantity")}
                  </p>
                  <div className="inline-flex items-center overflow-hidden rounded-lg border border-muted-accent bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={handleDecrement}
                      disabled={!showControls || quantity <= 1}
                      className="flex h-10 w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-orange-50 hover:text-orange-700 disabled:opacity-40"
                      aria-label="Azalt"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="flex h-10 min-w-[3rem] items-center justify-center border-x border-muted-accent bg-background text-base font-semibold">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      disabled={!showControls || quantity >= computedMax}
                      className="flex h-10 w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-orange-50 hover:text-orange-700 disabled:opacity-40"
                      aria-label="Artır"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {!showControls && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t("product.maxQuantityReached")}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t("product.totalAmount")}</p>
                  <p className="text-2xl font-bold text-foreground">{totalPrice}</p>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="mt-2 w-full">
            <Button
              type="submit"
              isLoading={isAdding}
              disabled={!isFormValid || isAdding || !showControls}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-orange-200 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition-colors hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
            >
              <ShoppingCart className="h-5 w-5" />
              <span>{t("product.addToCart")}</span>
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default AddToCartModal
